import type { WebPushSubscription } from "../shared/protocol";

export type PushState = "unsupported" | "needs-install" | "available" | "denied" | "enabled" | "unconfigured";

export interface PushSetup {
  state: PushState;
  publicKey?: string;
}

const deviceIdKey = "baby-monitor-device-id";

export function getDeviceId(): string {
  const existing = localStorage.getItem(deviceIdKey);
  if (existing) return existing;
  const created = crypto.randomUUID().replaceAll("-", "");
  localStorage.setItem(deviceIdKey, created);
  return created;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch {
    return null;
  }
}

export async function preparePush(): Promise<PushSetup> {
  const registration = await registerServiceWorker();
  const standalone = window.matchMedia("(display-mode: standalone)").matches;
  const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  if (!registration || !("PushManager" in window) || !("Notification" in window)) {
    return { state: isiOS && !standalone ? "needs-install" : "unsupported" };
  }
  if (isiOS && !standalone) return { state: "needs-install" };
  if (Notification.permission === "denied") return { state: "denied" };

  const response = await fetch("/api/push/public-key", { cache: "no-store" });
  if (!response.ok) return { state: "unconfigured" };
  const body = (await response.json()) as { publicKey?: string };
  if (!body.publicKey) return { state: "unconfigured" };

  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await saveSubscription(subscription);
    return { state: "enabled", publicKey: body.publicKey };
  }
  return { state: "available", publicKey: body.publicKey };
}

export async function enablePush(publicKey: string): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "available";

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToBytes(publicKey),
  });
  await saveSubscription(subscription);
  return "enabled";
}

async function saveSubscription(subscription: PushSubscription): Promise<void> {
  const value = subscription.toJSON();
  const body: WebPushSubscription = {
    endpoint: value.endpoint!,
    expirationTime: value.expirationTime ?? null,
    keys: {
      p256dh: value.keys!.p256dh!,
      auth: value.keys!.auth!,
    },
  };
  const response = await fetch("/api/push/subscription", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deviceId: getDeviceId(), subscription: body }),
  });
  if (!response.ok) throw new Error("The push subscription could not be saved.");
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value + "=".repeat((4 - (value.length % 4)) % 4);
  const binary = atob(padded.replaceAll("-", "+").replaceAll("_", "/"));
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}
